(ns arthur.ui.player
  "The rAF loop. Stage 6 and 7, driven.

  THE LOOP READS AND BLITS. It does not compute geometry, it does not build a
  resolver, and it does not wait on the event queue to paint. Per frame it reads
  the audio clock, applies a closure it already has, and writes bytes — a lookup
  and a blit, with no allocation beyond the op maps.

  It is not a Reagent component and must not become one. Stage 7 writes into a
  canvas from an animation frame; it is a sink, not a view that re-renders, and
  the actual content of the folklore about re-frame and canvas is that expensive
  work must not live in a layer-2 sub. The resolver is a layer-3 sub over the
  timeline, so the playhead moving cannot invalidate it.

  The one dispatch is `::playback/tick`, and it is deliberately NOT what the
  picture waits on: the frame is painted from the clock directly, and the tick
  only brings the document's playhead up to date so the readout and the scrubber
  agree with what is on screen. It fires when the derived frame CHANGES — thirty
  times a second at a 30fps clip on a 60Hz display, not sixty — and it carries
  no global interceptors."
  (:require [arthur.clock :as clock]
            [arthur.domain.clip :as clip]
            [arthur.domain.cut :as cut]
            [arthur.domain.outline :as outline]
            [arthur.domain.onion :as onion]
            [arthur.domain.palette :as pal]
            [arthur.domain.pick :as pick]
            [arthur.domain.raster :as raster]
            [arthur.domain.symbol :as symbol]
            [arthur.events.playback :as pb]
            [arthur.subs.playback :as sub]
            [arthur.subs.render :as render]
            [arthur.subs.ui :as ui-sub]
            [arthur.ui.canvas :as canvas]
            [arthur.ui.tracing :as tracing]
            [arthur.ui.layout :as layout]
            [re-frame.core :as rf]
            [re-frame.db :as rf-db]
            [reagent.ratom :as ratom]))

(defonce ^:private state
  (atom {:raf nil :canvas nil :raster nil :last -1}))

;; ---------------------------------------------------------------------------
;; the snapshot
;;
;; A PLAIN MAP that re-frame pushes into, which the loop reads without doing any
;; reactive work at all.
;;
;; This is not an optimisation, it is a correctness fix. A Reagent reaction
;; caches its value only while it has a watcher; deref it from outside a
;; reactive context — an rAF callback, say — and it RE-RUNS on every deref. So
;; `@(rf/subscribe [::render/resolver])` in the loop was rebuilding the resolver
;; sixty times a second: the topological order, the z paths, a cursor per
;; channel and a preallocated buffer per node, all of it, per frame. On the
;; five-node demo scene that was invisible. On a hundred-and-twenty-node scene
;; it was 3.6fps against a 170fps ceiling, and it presented as "the renderer is
;; slow" rather than as "the caching you assumed is not happening".
;;
;; `ratom/run!` keeps an always-active reaction, so the subscriptions it derefs
;; have a watcher and therefore cache, and the loop reads a plain atom.

;; Measured paint rate and mean clip-frames advanced per paint. A Reagent atom,
;; so the transport re-renders on it.
;;
;; `:drop` is the informative one. At 1.0 the loop is painting every frame the
;; clock asks for. Above it the clock is moving faster than the painting and
;; frames are being DROPPED — which is the designed failure rather than a bug,
;; but it is a thing to see rather than to infer.
(defonce meter (ratom/atom {:fps 0 :drop 0}))

(defonce ^:private meter-state
  (atom {:t0 nil :paints 0 :samples 0 :advanced 0 :prev nil}))

(def ^:private ^:const max-credible-advance
  ;; A jump bigger than this is a seek or a loop wrap, not a dropped frame. Both
  ;; move the playhead by an arbitrary amount in one tick, and counting either as
  ;; a drop makes the readout say the renderer is failing whenever the clip
  ;; restarts — which is exactly when a profile is running.
  16)

(defn- meter! [f]
  (let [now (js/performance.now)
        {:keys [t0 paints samples advanced prev]} @meter-state]
    (if (nil? t0)
      (reset! meter-state {:t0 now :paints 0 :samples 0 :advanced 0 :prev f})
      (let [d        (when prev (- f prev))
            credible (and d (pos? d) (<= d max-credible-advance))
            paints   (inc paints)
            samples  (if credible (inc samples) samples)
            advanced (if credible (+ advanced d) advanced)
            dt       (- now t0)]
        (if (>= dt 500)
          (do (reset! meter {:fps  (/ (* 1000 paints) dt)
                             :drop (if (pos? samples) (/ advanced samples) 0)})
              (reset! meter-state {:t0 now :paints 0 :samples 0 :advanced 0 :prev f}))
          (reset! meter-state {:t0 t0 :paints paints :samples samples
                               :advanced advanced :prev f}))))))

(defonce ^:private snapshot (atom {}))
(defonce ^:private tracker (atom nil))

(defn repaint!
  "Paint the frame again: something other than the frame changed what is seen,
  such as the work area scrolling."
  []
  (swap! state assoc :last -1))

(rf/reg-sub
 ::snapshot
 ;; ONE SUB, so the tracker below derefs one input that is never dirty when it
 ;; runs. It used to deref these one by one inside `ratom/run!`, and an edit
 ;; that also moves the selection ran it from the selection's change: it read
 ;; `::render/shown` while that was still the old resolver, then pulled the
 ;; dirty `::render/clip` further down, which re-ran it NESTED with the new one —
 ;; and the outer run, finishing last, wrote the old resolver back. The deleted
 ;; layer stayed on the stage until something else ran the tracker again. A
 ;; layer-3 sub is re-run from Reagent's queue instead, after its inputs.
 (fn [_ _]
   {:resolver  (rf/subscribe [::render/shown])
    :palette   (rf/subscribe [::render/palette])
    :preview   (rf/subscribe [::render/palette-preview])
    :solo?     (rf/subscribe [::render/palette-solo?])
    :fps       (rf/subscribe [::sub/fps])
    :frames    (rf/subscribe [::render/frames])
    :width     (rf/subscribe [::sub/width])
    :height    (rf/subscribe [::sub/height])
    :mat       (rf/subscribe [::layout/mat])
    :tracing   (rf/subscribe [::render/tracing])
    :frame     (rf/subscribe [::sub/frame])
    :playing?  (rf/subscribe [::sub/playing?])
    :range     (rf/subscribe [::sub/range])
    ;; The pen's draft is drawn INTO the picture, filled, so what is on screen
    ;; while placing points is the pixels a finished shape will be.
    :draft     (rf/subscribe [::ui-sub/draft])
    :hover     (rf/subscribe [::ui-sub/hover])
    :tone      (rf/subscribe [::ui-sub/tone])
    :fit       (rf/subscribe [::ui-sub/fit])
    ;; Where a new shape would land, so what is being drawn is drawn in that
    ;; symbol's stacking context.
    :target    (rf/subscribe [::ui-sub/creation-target])
    :onion     (rf/subscribe [::layout/onion])
    :selection (rf/subscribe [::ui-sub/selection])
    :ghosting  (rf/subscribe [::ui-sub/onion-scope])})
 (fn [{:keys [draft hover tone fit target] :as s} _]
   (-> s
       (dissoc :draft :hover :tone :fit :target)
       (assoc :pen {:draft draft :hover hover :tone tone :fit fit :target (:path target)}))))

(defn refresh-subs!
  "Build (or rebuild) the tracking reaction. Rebuilt on hot reload, because
  clearing the subscription cache orphans the reactions this holds."
  []
  (some-> @tracker ratom/dispose!)
  (reset! tracker
          (ratom/run!
           (let [before @snapshot
                 now    (reset! snapshot @(rf/subscribe [::snapshot]))]
             ;; A new resolver means a new scene or a new palette, and neither
             ;; moves the playhead — so nothing else would ask for a redraw.
             ;;
             ;; Switching tracing on or off wants the same redraw for the same
             ;; reason, and it needs asking for SEPARATELY: it is a viewing aid
             ;; in the editor's own state, so it changes what the canvases should
             ;; show without touching the frame number OR the resolver.
             (when-not (and (identical? (:resolver before) (:resolver now))
                            (= (select-keys before [:tracing :pen :mat :onion :selection :ghosting :playing? :preview :solo?])
                               (select-keys now [:tracing :pen :mat :onion :selection :ghosting :playing? :preview :solo?])))
               (repaint!))))))

;; The brush or eraser stroke being painted: its mask, mutated in place as the
;; pointer moves, which is why it is an atom the stage pokes rather than state
;; anything renders from. `{:mask :version :slot :knock :paths}`, `:mask` being an
;; `outline/mask`.
(defonce ^:private stroke (atom nil))
(defonce ^:private trace-cache (atom nil))

(defn stroke!
  "Show stroke `s` in the picture until it is replaced or nil, and redraw."
  [s]
  (reset! stroke s)
  (repaint!))

(defn set-canvas! [el]
  (swap! state assoc :canvas el)
  ;; The :ref fires AFTER the loop has started, so the first tick or two run
  ;; with nowhere to draw. Without this the loop would record frame 0 as
  ;; painted, find it unchanged on every subsequent tick, and never draw at all
  ;; — a blank canvas under a transport reading perfectly correct.
  (when el (repaint!)))

(defn set-onion-canvas! [el]
  (swap! state assoc :onion-canvas el)
  (when el (repaint!)))

(declare op-path reach)

(defn- paint-onions!
  "Ghost the picture `onion/frames` either side of `f`: the ops of
  `::ui-sub/onion-scope` — the selection's row, or the lane it is in —
  everything when nothing is selected. Each ghost is rasterised as
  far as it reaches inside `[vx vy wx wy]`, what is on screen, as the picture
  is, and the canvas covers them all."
  [f [vx vy wx wy]]
  (let [{:keys [onion-canvas]} @state
        {:keys [onion playing? resolver frames width height] path :ghosting} @snapshot
        ghosts (when (and onion-canvas (:on? onion) (not playing?))
                 (into []
                       (keep (fn [{:keys [frame direction]}]
                               ;; Consume each result before asking the resolver for
                               ;; another frame: its geometry buffers are shared
                               ;; with the current picture.
                               (let [ops (into [] (filter (fn [op]
                                                            (and (not= :trace (:kind op))
                                                                 (= path (vec (take (count path) (op-path op)))))))
                                               (resolver frame))
                                     [ax ay bx by] (reach ops width height)
                                     x0 (max ax vx) y0 (max ay vy) x1 (min bx wx) y1 (min by wy)]
                                 (when (and (< x0 x1) (< y0 y1))
                                   {:layer (raster/draw-ops! (assoc (raster/layer (- x1 x0) (- y1 y0)) :origin [x0 y0]) ops)
                                    :tint (if (= :before direction) [235 75 75] [65 145 245])}))))
                       (onion/frames f frames onion)))]
    (when onion-canvas
      (if (empty? ghosts)
        (canvas/clear-overlay! onion-canvas)
        (let [[x0 y0 x1 y1] (->> ghosts
                                 (map (fn [{{[ox oy] :origin :keys [w h]} :layer}] [ox oy (+ ox w) (+ oy h)]))
                                 (reduce (fn [[a b c d] [e f g h]] [(min a e) (min b f) (max c g) (max d h)])))]
          (when (not= (- x1 x0) (.-width onion-canvas)) (set! (.-width onion-canvas) (- x1 x0)))
          (when (not= (- y1 y0) (.-height onion-canvas)) (set! (.-height onion-canvas) (- y1 y0)))
          (canvas/clear-overlay! onion-canvas)
          (canvas/place! onion-canvas [x0 y0 x1 y1] [width height])
          (doseq [{:keys [layer tint]} ghosts]
            (let [[ox oy] (:origin layer)]
              (canvas/tint-layer! onion-canvas layer [(- ox x0) (- oy y0)] tint (:opacity onion)))))))))

(defn- reach
  "`[x0 y0 x1 y1]` round stage `w` by `h` and everything `ops` draw."
  [ops w h]
  (let [b #js [0 0 w h]
        grow! (fn [x0 y0 x1 y1]
                (aset b 0 (min (aget b 0) x0)) (aset b 1 (min (aget b 1) y0))
                (aset b 2 (max (aget b 2) x1)) (aset b 3 (max (aget b 3) y1)))]
    (doseq [{:keys [kind ^js pts n cx cy r size]} ops]
      (case kind
        :poly (dotimes [i n]
                (let [x (aget pts (* 2 i)) y (aget pts (inc (* 2 i)))] (grow! x y x y)))
        :disc (grow! (- cx r) (- cy r) (+ cx r) (+ cy r))
        :rect (let [s (/ size 2)] (grow! (- cx s) (- cy s) (+ cx s) (+ cy s)))
        nil))
    [(js/Math.floor (aget b 0)) (js/Math.floor (aget b 1))
     (js/Math.ceil (aget b 2)) (js/Math.ceil (aget b 3))]))

(defn- grow-board!
  "Make the work area hold `[x0 y0 x1 y1]` and a stage round it."
  [[x0 y0 x1 y1] w h]
  (layout/hold! [(- x0 w) (- y0 h) (+ x1 w) (+ y1 h)] [w h]))

(defn- view-of
  "`{:rect [x0 y0 x1 y1] :zoom z}`: the part of the work area on screen, in
  stage pixels, and screen pixels to a stage pixel, for a stage `w` pixels wide.
  Nil before the stage is laid out."
  [w]
  (when-let [^js c (:canvas @state)]
    (let [wrap (.-parentElement c)
          area (.closest c ".stage-area")
          s (.getBoundingClientRect wrap)
          a (.getBoundingClientRect area)
          z (/ (.-width s) w)]
      (when (pos? z)
        {:zoom z
         :rect [(js/Math.floor (/ (- (.-left a) (.-left s)) z))
                (js/Math.floor (/ (- (.-top a) (.-top s)) z))
                (js/Math.ceil (/ (- (+ (.-left a) (.-clientWidth area)) (.-left s)) z))
                (js/Math.ceil (/ (- (+ (.-top a) (.-clientHeight area)) (.-top s)) z))]}))))

(defn visible
  "The part of the work area on screen, `[x0 y0 x1 y1]` in stage pixels, for a
  stage `w` pixels wide. Nil before the stage is laid out."
  [w]
  (:rect (view-of w)))

(defn- raster-for
  "A raster `w` by `h` whose first pixel is stage point `origin`, its buffers
  reused while the size holds: scrolling moves the origin and nothing else. With
  coverage when it reaches off the stage, so what is there and drawn on by
  nothing can be left out of the blit."
  [w h origin off?]
  (let [k [w h off?]
        r (if (= k (:raster-key @state))
            (:raster @state)
            (:raster (swap! state assoc :raster-key k
                            :raster (if off? (raster/layer w h) (raster/make w h)))))]
    (assoc r :origin origin)))

(defn at
  "The topmost row path drawn at stage point `point` on the frame last painted,
  or nil. Read off the ops the canvas was drawn from, so a click picks exactly
  what is seen: their point buffers stay good until the next paint, and a
  pointer event is handled between two.

  `selected` is the row path selected now, which a light or a hole it belongs to
  does not hide from this: see `pick/hit-op`."
  ([point] (at point nil))
  ([point selected] (pick/hit (:ops @state) point selected)))

(defn hit-op
  "The topmost op that shows at `point`, as `at` picks it: what the eraser
  starts on."
  [point]
  (pick/hit-op (:ops @state) point))

(defn in-rect [rect depth]
  (pick/in-rect (:ops @state) rect depth))

(defn- traced
  "Stroke `s` as the rings letting go would make of it, `[outer & holes]` per
  piece: traced and simplified by the same function the saved shapes are, so
  the preview IS them. Once per version of the mask and fit, not per paint."
  [{:keys [mask version]} fit]
  (let [key [mask version fit]]
    (if (= key (:key @trace-cache))
      (:rings @trace-cache)
      (:rings (reset! trace-cache
                      {:key key
                       :rings (mapv #(outline/rings-of % fit) (outline/pieces mask))})))))

(defn- op-path [op] (let [n (:node op)] (if (vector? n) n [n])))

(defn- poly [ring op]
  (assoc op :kind :poly :pts (into-array ring) :n (quot (count ring) 2)))

(defn- cut-ops
  "`ops` with each op of a shape at one of `paths` cut by `cutters`, as the
  eraser will cut it."
  [ops paths cutters]
  (into []
        (mapcat (fn [{:keys [pts n] :as op}]
                  (or (when (and (= :poly (:kind op)) (contains? paths (op-path op)))
                        (some->> (cut/cut (vec (take (* 2 n) (array-seq pts))) cutters)
                                 (map #(poly % (dissoc op :pts :n)))))
                      [op])))
        ops))

(defn- inked
  "Op `op` drawn in tone `tone`: a slot's colour, or — for a remap — light on
  what is under it, through the same table the saved shape gets."
  [op tone]
  (if (symbol/remap? tone)
    (assoc op :lut (symbol/lut pal/slot tone) :color 0)
    (assoc op :color (pal/slot tone))))

(defn- with-previews
  "What is being drawn and is not in the document yet, added to `ops` in the
  stacking context of the symbol it will land in: the pen's draft as far as the
  pointer, a brush stroke as the polygons it will be, and an eraser's cut as
  the shapes it will leave."
  [ops]
  (let [{:keys [draft hover tone fit target]} (:pen @snapshot)
        target (or target [])
        pts (cond-> (vec draft) (and (seq draft) hover) (into hover))
        {:keys [slot knock paths] :as s} @stroke
        rings (when (:mask s) (traced s fit))]
    (cond-> ops
      (and (<= 6 (count pts)) (or (number? tone) (symbol/remap? tone)))
      (clip/atop target (inked (poly pts {}) tone))

      paths
      (cut-ops paths rings)

      (and rings (not paths) (nil? knock))
      (as-> ops (reduce #(clip/atop %1 target (inked (poly (outline/join %2) {}) slot))
                        ops rings))

      (and rings (not paths) knock)
      (as-> ops (reduce #(clip/in-layer %1 target (poly (outline/join %2) {:color 0 :knock knock}))
                        ops rings)))))

(defn paint!
  "Resolve `f` and put it on the canvas. `ops` are consumed here and only here —
  the resolver reuses its point buffers between frames, so they have to be
  rasterised before the next frame is asked for."
  [f]
  (let [{:keys [canvas]} @state
        {:keys [resolver palette width height tracing playing? mat preview solo?] :as snap} @snapshot
        tone (get-in snap [:pen :tone])
        solo (when (and solo? (number? tone)) tone)]
    (when (and canvas resolver width height)
      ;; User Timing, so a profile in the DevTools performance panel has named
      ;; spans in the Timings track instead of a wall of anonymous frames. Three
      ;; lines, and the difference between reading a profile and guessing at one.
      (js/performance.mark "arthur/paint:start")
      ;; THE STAGE IS THE CLIP'S, not a constant. Project dimensions are
      ;; independent of the footage, so the size the frame is rasterised at comes
      ;; out of the document like everything else.
      (let [{:keys [rect zoom]} (or (view-of width) {:rect [0 0 width height] :zoom 1})
            _ (paint-onions! f rect)
            {traces true picture false} (group-by #(= :trace (:kind %)) (resolver f))
            ;; A layer switched off, or all of them, is not on the stage at all:
            ;; not painted and not there to be clicked.
            traces (when (:on? tracing)
                     (into [] (remove #(contains? (:hidden tracing) (:layer %))) traces))
            active (clip/active-palette resolver)
            bg  pal/bg-slot]
        ;; The palette grid shows the frame's palette from here, the one place
        ;; it is decided, rather than working it out again.
        ;; Keyed by the resolver too, as a new tab brings a new one.
        (when (not= [resolver active] (:active @state))
          (swap! state assoc :active [resolver active])
          (rf/dispatch [::pb/frame-palette active]))
        ;; Soloed, only what is drawn in the chosen colour can be clicked.
        (swap! state assoc :ops (if solo
                                  (filterv #(or (#{:begin :end} (:kind %)) (= solo (:color %))) picture)
                                  (into (vec picture) traces)))
        ;; What is drawn, cut to what is on screen: a frame with nothing off the
        ;; stage costs what the stage does, and a drawing a dozen stages wide
        ;; costs no more than the pane it is seen through.
        (let [ops (with-previews picture)
              [ax ay bx by :as all] (reach ops width height)
              [vx vy wx wy] rect
              x0 (max ax vx) y0 (max ay vy) x1 (min bx wx) y1 (min by wy)]
          (grow-board! all width height)
          (js/performance.mark "arthur/blit:start")
          (if (and (< x0 x1) (< y0 y1))
            (let [off? (or (neg? x0) (neg? y0) (< width x1) (< height y1))
                  ras (raster-for (- x1 x0) (- y1 y0) [x0 y0] off?)]
              (-> ras (raster/clear! bg) (raster/draw-ops! ops))
              (canvas/blit! canvas ras
                            ;; THE frame's colours: the one palette it is in,
                            ;; or the one being previewed in its place.
                            (cond-> (if preview
                                      (pal/ramp-of (:hexes preview))
                                      (pal/effective-ramp palette active))
                              solo (pal/solo-ramp solo))
                            [width height]))
            (canvas/hide! canvas)))
        (tracing/paint! traces (assoc tracing :rect rect :zoom zoom :stage [width height] :playing? playing?) repaint!))
      (js/performance.measure "arthur/resolve+draw" "arthur/paint:start" "arthur/blit:start")
      (js/performance.measure "arthur/paint" "arthur/paint:start")
      ;; User Timing entries otherwise accumulate forever in the browser's
      ;; performance timeline during playback. DevTools captures the events as
      ;; they happen; keeping copies on the page serves no purpose.
      (js/performance.clearMarks "arthur/paint:start")
      (js/performance.clearMarks "arthur/blit:start")
      (js/performance.clearMeasures "arthur/resolve+draw")
      (js/performance.clearMeasures "arthur/paint"))))

(defonce ^:private watch
  ;; A scene swap changes the resolver and not the frame number, so the loop
  ;; would sit on an unchanged playhead and never redraw. Watching the reaction
  ;; is cheaper than making every event that can touch the scene remember to.
  (atom nil))

(defn- tick! []
  (let [{:keys [fps frames frame playing? range]} @snapshot
        live?  (clock/playing?)
        ready? (some? (:canvas @state))
        ;; DERIVED, never counted. A slow frame drops the frames it missed and
        ;; the next one lands where the audio already is, so the failure mode is
        ;; a visible stutter rather than an invisible slide out of sync.
        f      (if live? (clock/frame fps frames) frame)
        ;; PAST THE OUT MARK, BACK TO THE IN, as Blender's preview range loops.
        ;; Here rather than in either clock, so both backends loop the same
        ;; way, and before the paint, so the frame after the out is never shown.
        ;; CROSSING it, from a frame at or before it: played from past the out
        ;; mark, playback goes on from there.
        out    (or (:out range) (dec frames))
        wrap?  (and live? range (some-> (:played @state) (<= out)) (< out f))
        f      (if wrap? (or (:in range) 0) f)]
    (swap! state assoc :played (when live? f))
    (when wrap? (rf/dispatch [::pb/seek f]))
    ;; A paused scrub is an explicit seek, not a playback sample. Clear the
    ;; meter before the next play so its first frame cannot count the seek as
    ;; dropped frames or leave a stale low rate in the transport.
    (when (and (not live?) (:t0 @meter-state))
      (reset! meter-state {:t0 nil :paints 0 :samples 0 :advanced 0 :prev nil})
      (reset! meter {:fps 0 :drop 0}))
    ;; `ready?` gates the bookkeeping as well as the draw: recording a frame as
    ;; painted when it was not is how the canvas stays empty forever.
    (when (and ready? f (not= f (:last @state)))
      (swap! state assoc :last f)
      ;; A frame that cannot be drawn is REPORTED and the clock carries on. Let
      ;; it throw and the transport freezes with it — the playhead stops, the
      ;; stage stays on whatever it last showed, and the one line saying why is
      ;; buried under the next sixty identical ones. Reported once per message.
      (try
        (paint! f)
        (catch :default error
          (when (not= (ex-message error) (:failed @state))
            (swap! state assoc :failed (ex-message error))
            (js/console.error "arthur: frame" f "could not be drawn:" error)
            (rf/dispatch [::pb/paint-failed (ex-message error)]))))
      (when live? (meter! f))
      (when live? (rf/dispatch [::pb/tick f])))
    ;; The audio ending is the authority on playback having stopped; nothing
    ;; counts frames to notice it.
    ;; With marks, running off the end goes round again from the in.
    ;;
    ;; THE DB, NOT THE SNAPSHOT, says whether it is meant to be playing. The
    ;; snapshot catches up a frame late, so straight after a pause it still
    ;; said playing — and with marks that read as running off the end and
    ;; started it again, so pause never stuck while looping.
    (when (and (not live?) playing? (get-in @rf-db/app-db [:playback :playing?]))
      (rf/dispatch (if range [::pb/play] [::pb/pause])))))

(defn- frame-loop []
  ;; Scheduled BEFORE the tick, so nothing the tick does can end the loop.
  (swap! state assoc :raf (js/requestAnimationFrame frame-loop))
  (tick!))

(defn start! []
  (refresh-subs!)
  (when-not (:raf @state)
    (swap! state assoc :raf (js/requestAnimationFrame frame-loop))))

(defn stop! []
  (when-let [id (:raf @state)]
    (js/cancelAnimationFrame id)
    (swap! state assoc :raf nil)))
